/*
 * <scentt-tabs> \u2013 accessible tabs for the product tabs section.
 * Tabs: [role="tab"][aria-controls], panels: [role="tabpanel"]. Arrow buttons in the tab bar
 * (data-scentt-prev / data-scentt-next, handled by scentt-carousel.js) are re-pointed at the
 * rail of the visible panel.
 */
(() => {
  if (customElements.get('scentt-tabs')) return;
  const ID = /^[A-Za-z][\w-]{0,80}$/;

  class ScenttTabs extends HTMLElement {
    connectedCallback() {
      this.onClick = (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.select(tab);
      };
      this.onKey = (e) => {
        const tab = e.target.closest && e.target.closest('[role="tab"]');
        if (!tab || !this.contains(tab)) return;
        const tabs = this.tabs();
        let i = tabs.indexOf(tab);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') i += 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') i -= 1;
        else if (e.key === 'Home') i = 0;
        else if (e.key === 'End') i = tabs.length - 1;
        else return;
        e.preventDefault();
        this.select(tabs[(i + tabs.length) % tabs.length], true);
      };
      this.onBlockSelect = (e) => {
        const tab = e.target && e.target.closest && e.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.select(tab);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      const current = this.tabs().find((t) => t.getAttribute('aria-selected') === 'true');
      if (current) this.pointArrows(this.panelFor(current));
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    tabs() { return Array.from(this.querySelectorAll('[role="tab"]')); }

    panelFor(tab) {
      const id = tab.getAttribute('aria-controls');
      if (!id || !ID.test(id)) return null;
      const panel = this.querySelector(`#${CSS.escape(id)}`);
      return panel && this.contains(panel) ? panel : null;
    }

    select(tab, focus = false) {
      if (!tab) return;
      this.tabs().forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = this.panelFor(t);
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
      const panel = this.panelFor(tab);
      this.pointArrows(panel);
      this.dispatchEvent(new CustomEvent('scentt:tab-change', { bubbles: true, detail: { panel: panel ? panel.id : '' } }));
    }

    /** Point the shared arrows at the visible rail and refresh their disabled state. */
    pointArrows(panel) {
      const rail = panel && panel.querySelector('.scentt-rail[id]');
      if (!rail) return;
      this.querySelectorAll('[data-scentt-prev], [data-scentt-next]').forEach((btn) => {
        const attr = btn.hasAttribute('data-scentt-next') ? 'data-scentt-next' : 'data-scentt-prev';
        btn.setAttribute(attr, rail.id);
        btn.setAttribute('aria-controls', rail.id);
      });
      const carousel = rail.closest('scentt-carousel');
      if (carousel && typeof carousel.update === 'function') requestAnimationFrame(() => carousel.update());
    }
  }

  customElements.define('scentt-tabs', ScenttTabs);
})();
